import { of, Observable, from as observableFrom } from 'rxjs'; 
import { catchError, map, share, switchMap, concatMap, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects'; 
import { Store } from '@ngrx/store';

import { AppState } from '../../interfaces/app-state.interface';
import { TrackingActions } from './../tracking/tracking.action';
import { InfoproductsAction } from './infoproducts.action';
import { InfoproductsCourseService } from '../../services/infoproducts/infoproducts-course.service';
import { InfoproductsStrategyService } from '../../services/infoproducts/infoproducts-strategy.service';
import { InfoproductsPurchaseService } from '../../services/infoproducts/infoproducts-purchase.service';

@Injectable()
export class InfoproductsEffects {
  setLessonComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InfoproductsAction.setLessonComplete),
      concatMap(({ payload }) =>
        this.infoproductsCourseService
          .setLessonComplete(payload.courseId, payload.moduleId, payload.lessonId)
          .pipe(
            map(result => ({
              type: InfoproductsAction.setLessonCompleteSuccess,
              payload: result
            })),
            catchError(error =>
              of({
                type: InfoproductsAction.setLessonCompleteError,
                payload: { error }
              })
            )
          )
      ),
      share()
    )
  );

  /**
   * Busca todos os cursos disponíveis
   */
  getAvailableCourses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InfoproductsAction.getAvailableCourses),
      switchMap(() =>
        observableFrom(this.infoproductsCourseService.getAvailableCourses()).pipe(
          map(payload => {
            return {
              payload,
              type: InfoproductsAction.getAvailableCoursesSuccess
            };
          }),
          catchError(({ message }) =>
            of({ 
              type: InfoproductsAction.getAvailableCoursesError,
              payload: message
            })
          )
        )
      ),
      share()
    )
  );

  /**
   * Busca todos os cursos disponíveis
   */
  getAvailableStrategies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InfoproductsAction.getAvailableStrategies),
      switchMap(() =>
        observableFrom(
          this.infoproductsStrategyService.getAvailableStrategies()
        ).pipe(
          map(payload => ({
            payload,
            type: InfoproductsAction.getAvailableStrategiesSuccess
          })),
          catchError(({ message }) =>
            of({ 
              type: InfoproductsAction.getAvailableStrategiesError,
              payload: message
            })
          )
        )
      ),
      share()
    )
  );

  /**
   * Busca os produtos adquiridos do cliente
   */
  getCustomerProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InfoproductsAction.getCustomerProducts),
      switchMap(() =>
        observableFrom(
          this.infoproductsPurchaseService.getCustomerProducts()
        ).pipe(
          map(payload => ({
            payload,
            type: InfoproductsAction.getCustomerProductsSuccess
          })),
          catchError(({ message }) =>
            of({
              type: InfoproductsAction.getCustomerProductsError,
              payload: message
            })
          )
        )
      ),
      share()
    )
  );

  /**
   * Busca as subscriptions que o usuário assinou
   */
  trialRecommendations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InfoproductsAction.trialRecommendation),
      switchMap(action => {
        return this.infoproductsPurchaseService
          .trialRecommendation(action.payload)
          .pipe(
            map(trialPurchase => {
              this.store.dispatch({
                type: TrackingActions.trackEvents,
                payload: {
                  type: 'Trial Started',
                  events: {
                    product: action.payload.productName,
                    source: window.location.href,
                    isNewCustomer: false
                  }
                }
              });

              return {
                type: InfoproductsAction.trialRecommendationSuccess,
                payload: trialPurchase
              };
            }),
            catchError(({ error: { error }, message }) =>
              of({
                type: InfoproductsAction.trialRecommendationError,
                payload: (error && error.message) || message
              })
            )
          );
      }),
      share()
    )
  );

  constructor(
    private actions$: Actions<InfoproductsAction>,
    private infoproductsCourseService: InfoproductsCourseService,
    private infoproductsPurchaseService: InfoproductsPurchaseService,
    private infoproductsStrategyService: InfoproductsStrategyService,
    private store: Store<AppState>
  ) {}
}